import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment, { Moment } from "moment-timezone";
import {
  HiChevronLeft,
  HiChevronRight,
  HiCollection,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiPlus,
  HiX,
} from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import EmptyState from "../../../atoms/EmptyState";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import Spinner from "../../../atoms/Spinner";
import NoTranslate from "../../../atoms/NoTranslate";
import SegmentedTabs from "../../../molecules/SegmentedTabs";
import PersonFilter from "../todos/PersonFilter";
import { PickedUser } from "../todos/AssigneePicker";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  AppointmentSummary,
  useGetAgendaOverview,
  useRefreshAtHoldExpiry,
  useGetAgendaTypes,
  useGetMemberAppointments,
  useGetMemberSettings,
  useUpdateAgendaConfig,
} from "../../../../hooks/agenda";
import { useAgendaAccess } from "../../../../hooks/agenda/useAgendaAccess";
import WeekGrid from "./WeekGrid";
import MonthGrid from "./MonthGrid";
import AppointmentModal from "./AppointmentModal";
import AgendaConfigModal, {
  DEFAULT_NOTIFICATIONS,
  notificationsToSave,
} from "./AgendaConfigModal";
import AppointmentTypesPanel from "./AppointmentTypesPanel";
import AgendaSetupGuide from "./AgendaSetupGuide";
import {
  AgendaView,
  DEFAULT_DURATION_MINUTES,
  browserTimeZone,
  nextFullHour,
  periodLabel,
  anchorOnViewChange,
  stepUnit,
  viewRange,
} from "./agendaDates";
import { AppointmentDraft, prefilledParticipants } from "./appointmentDraft";
import { useAgendaFailure } from "./agendaTypes";

const ALL_MEMBERS = "all";

type Section = "calendar" | "types";

const initialView = (): AgendaView =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(max-width: 639px)").matches
    ? "day"
    : "week";

const PHONE_HEIGHT = "min-h-[32px] py-1.5 sm:min-h-[38px] sm:py-2";

const compactOnPhone = (label: string) => ({
  title: label,
  titleClassName: "hidden sm:flex",
  tooltip: label,
  "aria-label": label,
  className: `${PHONE_HEIGHT} px-1.5 sm:px-4`,
});

type ModalState =
  | { mode: "create"; draft: AppointmentDraft }
  | { mode: "edit"; appointment: AppointmentSummary }
  | null;

const AgendaPage: React.FC = () => {
  const { t } = useTranslation();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;
  const selfId = user.user_identification;
  const permissions = usePermissions(user);

  const {
    hasFeature,
    canWrite,
    managesOthers,
    canConfigure,
    config: {
      data: config,
      isLoading: configLoading,
      isError: configError,
      refetch: refetchConfig,
    },
  } = useAgendaAccess(customerId, entityId);
  const canSearch = permissions.can(Cap.SEARCH, Level.VIEW);
  const canEmail = permissions.hasFeatureAccess("SEND_MESSAGES");
  const canSms = permissions.hasFeatureAccess("SEND_SMS");

  const [view, setView] = useState<AgendaView>(initialView);
  const [anchor, setAnchor] = useState<Moment>(() => moment().startOf("day"));
  const [memberFilter, setMemberFilter] = useState<string>(
    managesOthers ? ALL_MEMBERS : selfId
  );
  const [personFilter, setPersonFilter] = useState<PickedUser | null>(null);
  const memberFilterId = useId();
  const personFilterId = useId();
  const [modal, setModal] = useState<ModalState>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("section");
  const section: Section = requestedSection === "types" ? "types" : "calendar";
  const showTypes = section === "types";
  const openTypeId = searchParams.get("type");

  const openTypes = (typeId: string | null = null) =>
    setSearchParams(
      { section: "types", ...(typeId ? { type: typeId } : {}) },
      { replace: !typeId && !!openTypeId }
    );
  const openSection = (next: Section) =>
    setSearchParams(next === "calendar" ? {} : { section: next });

  const activate = useUpdateAgendaConfig(customerId, entityId);
  const agendaOn = Boolean(config?.enabled);

  const showGuide = agendaOn && canConfigure && section === "calendar";
  const { data: types } = useGetAgendaTypes(customerId, entityId, {
    enabled: showGuide,
  });

  const range = useMemo(() => viewRange(view, anchor), [view, anchor]);
  const viewedMember = memberFilter === ALL_MEMBERS ? null : memberFilter;

  // Moving through periods keeps the last week on screen while the next one loads;
  // another member or person must never show the previous one's appointments.
  const filterKey = `${viewedMember ?? ""}|${personFilter?.id ?? ""}`;
  const [loadedFilterKey, setLoadedFilterKey] = useState(filterKey);
  const overviewQuery = useGetAgendaOverview(
    customerId,
    entityId,
    {
      from: range.from,
      to: range.to,
      members: viewedMember ? [viewedMember] : undefined,
      person: personFilter?.id,
    },
    {
      enabled: agendaOn && managesOthers && section === "calendar",
      keepPreviousData: loadedFilterKey === filterKey,
    }
  );
  const overviewLoaded =
    overviewQuery.isSuccess && !overviewQuery.isPreviousData;
  useEffect(() => {
    if (overviewLoaded) setLoadedFilterKey(filterKey);
  }, [overviewLoaded, filterKey]);
  const ownQuery = useGetMemberAppointments(
    customerId,
    entityId,
    selfId,
    { from: range.from, to: range.to },
    { enabled: agendaOn && !managesOthers }
  );
  const appointmentsQuery = managesOthers ? overviewQuery : ownQuery;

  const appointments = useMemo(() => {
    const list = appointmentsQuery.data ?? [];
    if (managesOthers || !personFilter) return list;
    return list.filter((a) =>
      a.participants.some((p) => p.user_identification === personFilter.id)
    );
  }, [appointmentsQuery.data, managesOthers, personFilter]);
  useRefreshAtHoldExpiry(entityId, appointmentsQuery.data ?? []);

  const { data: viewedSettings, refetch: refetchViewedSettings } =
    useGetMemberSettings(customerId, entityId, viewedMember ?? "", {
      enabled: agendaOn && !!viewedMember,
    });

  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refetchConfig(),
        appointmentsQuery.refetch(),
        ...(viewedMember ? [refetchViewedSettings()] : []),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const members = config?.members ?? [];

  const step = (direction: 1 | -1) =>
    setAnchor((prev) => prev.clone().add(direction, stepUnit(view)));

  const changeView = (next: AgendaView) => {
    if (next === view) return;
    setAnchor((prev) => anchorOnViewChange(view, prev));
    setView(next);
  };

  const openCreate = (start: number) => {
    if (!canWrite) return;
    setModal({
      mode: "create",
      draft: {
        start,
        end: moment(start).add(DEFAULT_DURATION_MINUTES, "minute").valueOf(),
        participantIds: prefilledParticipants(
          { id: selfId, managesOthers },
          viewedMember ? [viewedMember] : []
        ),
      },
    });
  };

  const openCreateNow = () =>
    openCreate(
      view === "day" && !anchor.isSame(moment(), "day")
        ? anchor.clone().hour(9).startOf("hour").valueOf()
        : nextFullHour()
    );

  const onActivate = () =>
    activate.mutate(
      {
        enabled: true,
        timezone: config?.timezone || browserTimeZone(),
        notifications: notificationsToSave(
          { ...DEFAULT_NOTIFICATIONS, ...config?.notifications },
          canEmail,
          canSms
        ),
      },
      {
        onSuccess: () => {
          toast.success(t("agenda.activation.done") as string);
        },
        onError: (err) => {
          toast.error(failure(err, "agenda.activation.error"));
        },
      }
    );

  const header = (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold text-gray-900">
          {t("agenda.pageTitle")}
        </h1>
        <p className="mt-0.5 text-sm text-gray-500">
          {showTypes
            ? t("agenda.types.pageSubtitle")
            : agendaOn && !appointmentsQuery.isLoading
            ? t("agenda.count", { count: appointments.length })
            : t("agenda.subtitle")}
        </p>
      </div>
      {agendaOn && (
        <div className="flex flex-wrap items-center gap-2">
          {canConfigure && (
            <CustomButton
              {...compactOnPhone(t("agenda.actions.config") as string)}
              variant="secondary"
              icon="settings"
              onClick={() => setShowConfig(true)}
            />
          )}
          {section === "calendar" && canWrite && (
            <CustomButton
              variant="primary"
              className={`${PHONE_HEIGHT} gap-0 px-3 sm:gap-2 sm:px-4`}
              icon={<HiPlus size={20} className="hidden sm:block" />}
              title={t("agenda.actions.new") as string}
              onClick={openCreateNow}
            />
          )}
        </div>
      )}
    </div>
  );

  if (!hasFeature) {
    return (
      <div className="container mx-auto px-3 py-6">
        {header}
        <FeatureUpgradePrompt
          featureName={t("agenda.pageTitle") as string}
          featureDescription={t("agenda.upgradeDescription") as string}
          onUpgrade={() => window.dispatchEvent(new Event("openPlansModal"))}
        />
      </div>
    );
  }

  let body: React.ReactNode;
  if (configLoading) {
    body = (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  } else if (configError || !config) {
    body = (
      <EmptyState
        bordered
        icon={<HiOutlineCalendar size={24} />}
        title={t("agenda.loadError")}
        action={
          <CustomButton
            variant="secondary"
            title={t("agenda.retry") as string}
            onClick={() => refetchConfig()}
          />
        }
      />
    );
  } else if (!config.enabled) {
    body = (
      <EmptyState
        bordered
        icon={<HiOutlineCalendar size={24} />}
        title={
          canConfigure
            ? t("agenda.activation.ownerTitle")
            : t("agenda.activation.memberTitle")
        }
        description={
          canConfigure
            ? t("agenda.activation.ownerDescription")
            : t("agenda.activation.memberDescription")
        }
        action={
          canConfigure ? (
            <CustomButton
              variant="primary"
              title={t("agenda.activation.action") as string}
              onClick={onActivate}
              isLoading={activate.isLoading}
              disabled={activate.isLoading}
            />
          ) : undefined
        }
      />
    );
  } else if (showTypes) {
    body = (
      <AppointmentTypesPanel
        openTypeId={openTypeId}
        onOpenType={(typeId) => openTypes(typeId)}
      />
    );
  } else {
    const tabs = [
      { key: "day" as const, label: t("agenda.views.day") as string },
      { key: "week" as const, label: t("agenda.views.week") as string },
      { key: "month" as const, label: t("agenda.views.month") as string },
    ];
    body = (
      <>
        {showGuide && types && (
          <AgendaSetupGuide
            types={types}
            onCreateType={() => openTypes("new")}
          />
        )}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <SegmentedTabs
            tabs={tabs}
            active={view}
            onChange={changeView}
            stripMaxWidth="max-w-sm"
          />

          <div className="flex flex-col gap-3 border-b border-gray-100 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:px-4">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:basis-64">
              <CustomButton
                variant="secondary"
                size="sm"
                className="px-1.5"
                icon={<HiChevronLeft size={20} />}
                aria-label={t("agenda.nav.previous") as string}
                onClick={() => step(-1)}
              />
              <CustomButton
                variant="secondary"
                size="sm"
                title={t("agenda.nav.today") as string}
                onClick={() => setAnchor(moment().startOf("day"))}
              />
              <CustomButton
                variant="secondary"
                size="sm"
                className="px-1.5"
                icon={<HiChevronRight size={20} />}
                aria-label={t("agenda.nav.next") as string}
                onClick={() => step(1)}
              />
              <CustomButton
                {...compactOnPhone(t("agenda.refresh") as string)}
                variant="secondary"
                size="sm"
                className="px-1.5 sm:px-3"
                icon={
                  <HiOutlineRefresh
                    size={20}
                    className={refreshing ? "animate-spin" : undefined}
                  />
                }
                disabled={refreshing}
                onClick={refresh}
              />
              <h2
                className="min-w-0 break-words text-base font-semibold text-gray-900"
                aria-live="polite"
              >
                {periodLabel(view, anchor, t)}
              </h2>
              {appointmentsQuery.isFetching && !refreshing && (
                <Spinner size="sm" />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {managesOthers && (
                <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
                  <label
                    htmlFor={memberFilterId}
                    className="shrink-0 text-sm text-gray-600"
                  >
                    {t("agenda.filters.member")}
                  </label>
                  <select
                    id={memberFilterId}
                    value={memberFilter}
                    onChange={(e) => setMemberFilter(e.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white py-1 pl-3 pr-8 text-base text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:flex-none md:py-1.5 md:text-sm"
                  >
                    <option value={ALL_MEMBERS}>
                      {t("agenda.filters.allMembers")}
                    </option>
                    {!members.some((m) => m.user_identification === selfId) && (
                      <option value={selfId}>{t("agenda.filters.me")}</option>
                    )}
                    {members.map((m) => (
                      <option
                        key={m.user_identification}
                        value={m.user_identification}
                      >
                        {m.user_identification === selfId
                          ? t("agenda.filters.meNamed", { name: m.name })
                          : m.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {canSearch && (
                <div
                  role="group"
                  aria-labelledby={personFilterId}
                  className="flex w-full min-w-0 items-center gap-2 sm:w-auto"
                >
                  <span
                    id={personFilterId}
                    className="shrink-0 text-sm text-gray-600"
                  >
                    {t("agenda.filters.person")}
                  </span>
                  {personFilter ? (
                    <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border border-blue-300 bg-blue-50 py-1 pl-3 pr-1 text-sm font-medium text-blue-700">
                      <span className="truncate">
                        <NoTranslate>{personFilter.name}</NoTranslate>
                      </span>
                      <CustomButton
                        variant="none"
                        size="icon"
                        className="rounded-md text-blue-600 enabled:hover:text-blue-800"
                        icon={<HiX size={14} />}
                        aria-label={t("agenda.filters.clearPerson") as string}
                        onClick={() => setPersonFilter(null)}
                      />
                    </span>
                  ) : (
                    <PersonFilter
                      customerId={customerId}
                      entityId={entityId}
                      selectedIds={[]}
                      onAdd={setPersonFilter}
                      label={t("agenda.filters.choosePerson") as string}
                      showCount={false}
                      align="auto"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {appointmentsQuery.isError ? (
            <EmptyState
              title={t("agenda.appointmentsError")}
              action={
                <CustomButton
                  variant="secondary"
                  title={t("agenda.retry") as string}
                  onClick={() => appointmentsQuery.refetch()}
                />
              }
            />
          ) : view === "month" ? (
            <MonthGrid
              days={range.days}
              month={anchor}
              appointments={appointments}
              canCreate={canWrite}
              onCreate={openCreate}
              onOpen={(appointment) => setModal({ mode: "edit", appointment })}
              onSelectDay={(day) => {
                setAnchor(day.clone().startOf("day"));
                setView("day");
              }}
            />
          ) : (
            <WeekGrid
              days={range.days}
              appointments={appointments}
              canCreate={canWrite}
              showMembers={!viewedMember}
              availability={viewedMember ? viewedSettings : undefined}
              onCreate={openCreate}
              onOpen={(appointment) => setModal({ mode: "edit", appointment })}
            />
          )}
        </div>
      </>
    );
  }

  const sectionTabs: {
    key: Section;
    label: string;
    short: string;
    icon: React.ReactNode;
  }[] = [
    {
      key: "calendar",
      label: t("agenda.sections.calendar"),
      short: t("agenda.sections.calendar"),
      icon: <HiOutlineCalendar size={18} aria-hidden="true" />,
    },
    {
      key: "types",
      label: t("agenda.sections.types"),
      short: t("agenda.sections.typesShort"),
      icon: <HiCollection size={18} aria-hidden="true" />,
    },
  ];

  return (
    <div className="container mx-auto px-3 py-6">
      {header}
      {agendaOn && (
        <div className="mb-4 border-b border-gray-200">
          <nav
            className="-mb-px flex gap-4 overflow-x-auto sm:gap-6"
            aria-label={t("agenda.sections.label") as string}
          >
            {sectionTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => openSection(tab.key)}
                aria-label={tab.label}
                aria-current={section === tab.key ? "page" : undefined}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  section === tab.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {tab.icon}
                <span className="sm:hidden">{tab.short}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}
      {body}

      <AppointmentModal
        show={modal !== null}
        onClose={() => setModal(null)}
        appointment={modal?.mode === "edit" ? modal.appointment : null}
        draft={modal?.mode === "create" ? modal.draft : undefined}
        openedFrom={personFilter?.id ?? viewedMember}
      />

      {config && canConfigure && (
        <AgendaConfigModal
          show={showConfig}
          onClose={() => setShowConfig(false)}
          config={config}
        />
      )}
    </div>
  );
};

export default AgendaPage;
